import type { RouterConfig } from "@nuxt/schema";

// https://router.vuejs.org/api/interfaces/routeroptions.html
export default <RouterConfig>{
  async scrollBehavior(to, from, savedPosition) {
    // Prevent automatic scroll behavior when navigating between projects list and detail
    // Scroll restoration is handled manually in ExperienceDetailsDrawer component
    if (
      (to.path === "/projects" && from.path.startsWith("/projects/")) ||
      (to.path.startsWith("/projects/") && from.path === "/projects")
    ) {
      return false;
    }

    // For saved positions (browser back/forward), use them
    if (savedPosition) {
      return savedPosition;
    }

    // For hash links, scroll to the element
    if (to.hash) {
      const hash = decodeURIComponent(to.hash);
      const maxAttempts = 20;
      const delayMs = 25;

      // When navigating from another route, the target might not be in the DOM yet.
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const target = document.querySelector(hash) as HTMLElement | null;

        if (target) {
          const navHeightCss =
            getComputedStyle(document.documentElement).getPropertyValue(
              "--main-nav-height"
            ) || "0";
          const navHeight = Number.parseFloat(navHeightCss) || 0;

          // Prefer the element's own scroll-margin-top (accounts for
          // additional sticky sub-navs) over the bare main-nav height.
          const scrollMarginTop =
            Number.parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
          const offset = scrollMarginTop > 0 ? scrollMarginTop : navHeight;

          const top =
            target.getBoundingClientRect().top + window.scrollY - offset;

          return {
            top: Math.max(0, top),
            behavior: "smooth",
          };
        }

        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }

      // Fallback to top if hash target was not found after waiting.
      return { top: 0 };
    }

    // Default: scroll to top for new pages
    return { top: 0 };
  },
};
